// CB:BEGIN admin-projects-page
import React, { useEffect, useState } from 'react';
import ResponsiveContainer from '@/components/layout/ResponsiveContainer';
import RequireAdmin from '@/routes/RequireAdmin';
import { useApi } from '@/lib/useApi';

type ProjectStatus = 'BIDDING' | 'IN_PROGRESS' | 'COMPLETED';

interface Project {
  id: string;
  title?: string;
  address?: string;
  homeownerId?: string;
  status: ProjectStatus | string;
  createdAt: string;
  updatedAt?: string;
  roofColor?: string | null;
  contractSigned?: boolean;
  projectStartAt?: string | null;
  escrow?: { id: string; status: string; totalAmountCents: number; releasedCents: number } | null;
  acceptedContractorId?: string | null;
}

export default function AdminProjectsPage() {
  const api = useApi();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<{ items?: Project[]; projects?: Project[] }>('/api/admin/projects');
        setProjects(res?.items ?? res?.projects ?? []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <RequireAdmin>
      <ResponsiveContainer className="p-6" data-testid="projects-mgmt-page-root">
        <h1 className="text-2xl font-semibold mb-4">Project Management</h1>
        {loading ? (
          <div>Loading projects…</div>
        ) : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left">Project</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Escrow</th>
                  <th className="px-4 py-2 text-left">Signed</th>
                  <th className="px-4 py-2 text-left">Color</th>
                  <th className="px-4 py-2 text-left">Created</th>
                </tr>
              </thead>
              <tbody>
                {projects.map(p => (
                  <tr key={p.id} className="border-t">
                    <td className="px-4 py-2">
                      <a href={`/admin/projects/${p.id}`} className="text-blue-600 hover:underline">
                        {p.title ?? p.address ?? p.id}
                      </a>
                    </td>
                    <td className="px-4 py-2">{p.status}</td>
                    <td className="px-4 py-2">
                      {p.escrow
                        ? `${p.escrow.status} · $${((p.escrow.totalAmountCents - (p.escrow.releasedCents ?? 0)) / 100).toFixed(2)}`
                        : '—'}
                    </td>
                    <td className="px-4 py-2">{p.contractSigned ? 'Yes' : 'No'}</td>
                    <td className="px-4 py-2">{p.roofColor ?? '—'}</td>
                    <td className="px-4 py-2">{new Date(p.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
                {projects.length === 0 && (
                  <tr><td className="px-4 py-6 text-gray-500" colSpan={6}>No projects found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </ResponsiveContainer>
    </RequireAdmin>
  );
}
// CB:END admin-projects-page
